import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import {
  addCrossClientActiveEvaluation,
  nextStepKindForResult,
  selectCrossClientResolversInFlight,
  selectCrossClientResultsExecution,
  startCrossClientExecutionPolling,
} from '../../../../store/crossClient/crossClientResults'
import { useLazyGetAllCrossClientQueryExecutionResultsQuery, useLazyGetCrossClientQueryExecutionResultsQuery } from '../../../../store/crossClient/crossClient'
import { useCreateCrossClientQueryEvaluationMutation } from '../../../../store/crossClient/crossClientExecution'
import {
  CrossClientQueryEvaluationTypes,
  CrossClientQueryResultOrderBys,
  type CrossClientQueryEvaluation,
  type CrossClientQueryEvaluationType,
  type CrossClientQueryResult,
} from '../../../../models/CrossClient'
import { addToast } from '../../../../store/toast/toastSlice'
import { awaitsResolvers } from '../../../../store/crossClient/utils'
import { CROSS_CLIENT_FUNDS_TOAST_MESSAGE, isCrossClientFundsQuotaError } from '../../utils/crossClientFunds'
import { useCrossClientAiPhaseAccess } from '../../utils/crossClientAiPhaseAccess'
import { cardFilter, type CcqCard } from './ccqResultView'
import { collectResultsByIds } from './useCcqCardResults'

// Why a selection's AI part didn't run, beyond having no AI-eligible clients; each caller words it itself.
export type CrossClientSelectionAiBlockReason = 'account-unavailable' | 'not-permitted'

export interface CrossClientSelectionResult {
  aiBlockedReason?: CrossClientSelectionAiBlockReason
  evaluation: CrossClientQueryEvaluation | null
  // The part of the selection the batch took: clients already checked or under way are left out.
  sentResultIds?: string[]
}

export interface CrossClientEvaluationActions {
  evaluateResults: (resultIds: string[], type: CrossClientQueryEvaluationType) => Promise<CrossClientQueryEvaluation | null>
  evaluateOutstanding: (type: CrossClientQueryEvaluationType, limit?: number) => Promise<CrossClientQueryEvaluation | null>
  evaluateSelectionSplit: (resultIds: string[]) => Promise<CrossClientSelectionResult>
}

// `card` only scopes evaluateOutstanding; single results and selections don't need it.
export const useCrossClientEvaluationActions = (card: CcqCard | null): CrossClientEvaluationActions => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const execution = useAppSelector(selectCrossClientResultsExecution)
  const inFlight = useAppSelector(selectCrossClientResolversInFlight)
  const { hasAiPhaseAccess, accountLoading, accountError, retryAiPhaseAccess } = useCrossClientAiPhaseAccess()

  const [triggerGetAllResults] = useLazyGetAllCrossClientQueryExecutionResultsQuery()
  const [triggerGetResultsPage] = useLazyGetCrossClientQueryExecutionResultsQuery()
  const [createEvaluation] = useCreateCrossClientQueryEvaluationMutation()

  const runEvaluation = useCallback(
    async (resultIds: string[], type: CrossClientQueryEvaluationType): Promise<CrossClientQueryEvaluation | null> => {
      if (!execution?.id || !resultIds.length) {
        return null
      }
      try {
        const evaluation = await createEvaluation({ accountId, executionId: execution.id, type, resultIds }).unwrap()
        dispatch(addCrossClientActiveEvaluation(evaluation))
        dispatch(startCrossClientExecutionPolling())
        return evaluation
      } catch (e) {
        if (isCrossClientFundsQuotaError(e)) {
          dispatch(addToast({ variant: 'error', title: 'Not enough credits', messages: [CROSS_CLIENT_FUNDS_TOAST_MESSAGE] }))
        } else {
          console.error(e)
        }
        return null
      }
    },
    [accountId, execution, createEvaluation, dispatch],
  )

  const isOutstanding = useCallback(
    (type: CrossClientQueryEvaluationType) => (result: CrossClientQueryResult) =>
      !result.activeEvaluationId && !awaitsResolvers(execution, result, inFlight) && nextStepKindForResult(result) === type,
    [execution, inFlight],
  )

  const collectOutstandingPaged = useCallback(
    async (type: CrossClientQueryEvaluationType, limit: number, executionId: string, scopedCard: CcqCard): Promise<string[]> => {
      if (!execution) {
        return []
      }
      const filter = cardFilter(scopedCard, execution, inFlight)
      const collect = async (page: number, ids: string[]): Promise<string[]> => {
        const response = await triggerGetResultsPage({
          accountId,
          executionId,
          ...filter,
          orderBy: CrossClientQueryResultOrderBys.LOCAL_RESOLVER_SCORE,
          page,
          size: limit,
        }).unwrap()
        const next = [...ids, ...response.data.filter(isOutstanding(type)).map((result) => result.id)].slice(0, limit)
        return next.length >= limit || page + 1 >= response.page.totalPages ? next : collect(page + 1, next)
      }
      return collect(0, [])
    },
    [accountId, execution, inFlight, isOutstanding, triggerGetResultsPage],
  )

  const evaluateOutstanding = useCallback(
    async (type: CrossClientQueryEvaluationType, limit?: number): Promise<CrossClientQueryEvaluation | null> => {
      if (!execution?.id || card === null) {
        return null
      }
      try {
        if (limit) {
          return await runEvaluation(await collectOutstandingPaged(type, limit, execution.id, card), type)
        }
        const response = await triggerGetAllResults({
          accountId,
          executionId: execution.id,
          ...cardFilter(card, execution, inFlight),
          orderBy: CrossClientQueryResultOrderBys.NEXT_STEP,
        }).unwrap()
        return await runEvaluation(
          response.data.filter(isOutstanding(type)).map((result) => result.id),
          type,
        )
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, execution, inFlight, card, triggerGetAllResults, collectOutstandingPaged, runEvaluation, isOutstanding],
  )

  const evaluateSelectionSplit = useCallback(
    async (resultIds: string[]): Promise<CrossClientSelectionResult> => {
      if (!execution?.id || !resultIds.length) {
        return { evaluation: null }
      }
      try {
        const results = await collectResultsByIds(triggerGetAllResults, { accountId, executionId: execution.id }, resultIds)
        const aiIds = results.filter(isOutstanding(CrossClientQueryEvaluationTypes.AI)).map((result) => result.id)
        if (!aiIds.length) {
          return { evaluation: null }
        }
        if (accountError) {
          // The action is already under way, so an account-query error (that query doesn't retry itself) gets one
          // recheck. A recheck that still leaves the user out is a denial, not a failure to check.
          const retryResult = await retryAiPhaseAccess()
          if (retryResult !== 'granted') {
            return { aiBlockedReason: retryResult === 'denied' ? 'not-permitted' : 'account-unavailable', evaluation: null }
          }
        } else if (accountLoading) {
          // The initial load isn't retried: it settles on its own.
          return { aiBlockedReason: 'account-unavailable', evaluation: null }
        } else if (!hasAiPhaseAccess) {
          return { aiBlockedReason: 'not-permitted', evaluation: null }
        }
        const evaluation = await runEvaluation(aiIds, CrossClientQueryEvaluationTypes.AI)
        return { evaluation, sentResultIds: aiIds }
      } catch (e) {
        console.error(e)
        return { evaluation: null }
      }
    },
    [accountId, execution, triggerGetAllResults, runEvaluation, isOutstanding, hasAiPhaseAccess, accountLoading, accountError, retryAiPhaseAccess],
  )

  return { evaluateResults: runEvaluation, evaluateOutstanding, evaluateSelectionSplit }
}
